import * as React from "react"

import { X } from "lucide-react"
import { cn } from "@/lib/utils"

// MAIUSCOLO automatico globale sui campi testo del gestionale.
// Escludiamo i campi dove non ha senso: email/PEC, password, URL, IBAN, codice fiscale
// (già gestito a monte), campi tecnici (host/utente/porta/dominio/api/token), ricerche e
// campi numerici/data. Le note lunghe/descrizioni usano <Textarea> e non passano di qui.
const NO_UPPER_TYPES = new Set([
  "email", "password", "url", "number", "tel", "search",
  "date", "time", "datetime-local", "month", "week", "color", "range", "file",
]);
const NO_UPPER_RE = /e-?mail|\bpec\b|iban|\burl\b|http|link|sito|\bweb\b|password|\bpwd\b|codice[\s_-]?fiscale|(^|[-_])cf($|[-_])|\bhost\b|server|\bport|porta|dominio|domain|\bapi\b|token|secret|slug|username|user-?name|\butente\b|ricerca|search|cerca|filtro|note|descriz|\bnota\b|titolo|title|template|cartella|folder|sezione|section|subject|oggetto|indirizzo|address/i;

function shouldUppercase(type, props) {
  if (/iban/i.test([props["data-testid"], props.name, props.id, props.placeholder].filter(Boolean).join(" "))) return true;
  if (props.noUppercase || props["data-nouppercase"]) return false;
  if (type && NO_UPPER_TYPES.has(type)) return false;
  const im = props.inputMode || "";
  if (["email", "url", "tel", "numeric", "decimal"].includes(im)) return false;
  const hay = [props["data-testid"], props.name, props.id, props.placeholder, props.autoComplete]
    .filter(Boolean).join(" ").toLowerCase();
  return !NO_UPPER_RE.test(hay);
}

const Input = React.forwardRef(({ className, type, onChange, noUppercase, ...props }, ref) => {
  const handleChange = onChange
    ? (e) => {
        if (shouldUppercase(type, { ...props, noUppercase })) {
          const up = e.target.value.toUpperCase();
          if (up !== e.target.value) {
            const el = e.target;
            const s = el.selectionStart, en = el.selectionEnd;
            el.value = up;
            // Mantiene il cursore dove l'utente sta scrivendo (non salta alla fine).
            try { if (s != null) el.setSelectionRange(s, en); } catch { /* tipo senza selezione */ }
          }
        }
        onChange(e);
      }
    : undefined;
  if (type === "date" && onChange) {
    const cls = (className || "").split(/\s+/);
    const isMargin = (c) => /^-?m[tbxylr]?-/.test(c);
    const clear = () => onChange({ target: { value: "", name: props.name }, currentTarget: { value: "" } });
    return (
      <div className={cn("relative", cls.filter(isMargin).join(" "))}>
        <input type="date" onChange={onChange} ref={ref} {...props}
          className={cn("block h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-14 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", cls.filter((c) => !isMargin(c)).join(" "), "pr-14")} />
        {props.value && !props.disabled && !props.readOnly && (
          <button type="button" tabIndex={-1} onClick={clear} aria-label="Cancella data" title="Cancella data"
            data-testid={props["data-testid"] ? `${props["data-testid"]}-clear` : undefined}
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-red-500 hover:bg-slate-500/10">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }
  return (
    <input
      type={type}
      onChange={handleChange}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
